/**
 * View-model hook for user-driven swimlane selection: agent-card clicks
 * (span id → row key) and inline fork-navigator clicks (which preserve the
 * clicked element's viewport position via a scroll anchor), plus the
 * pending-scroll-target signal that suppresses scroll-to-top on selection
 * change.
 */

import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type RefObject,
} from "react";

import { type TimelineState } from "../timeline/hooks";
import {
  buildSpanSelectKeys,
  type SpanSelectKey,
} from "../timeline/timelineEventNodes";

export interface UseSelectionActionsOptions {
  /** Timeline selection state (rows + select). */
  timelineState: TimelineState;
  /** The transcript's scroll container. */
  scrollRef: RefObject<HTMLDivElement | null>;
  /** Deep-link props (a pending deep link is a pending scroll target). */
  initialEventId?: string | null;
  initialMessageId?: string | null;
}

export interface SelectionActions {
  /** Span ID → swimlane selection key lookup (also feeds deep-link resolution). */
  spanSelectKeys: ReadonlyMap<string, SpanSelectKey>;
  /** Select the swimlane row rendering `spanId` (agent card clicks). */
  selectBySpanId: (spanId: string) => void;
  /** Select a row by key. With an anchor element (inline fork-navigator
   *  clicks), the current scroll position is captured and restored after the
   *  selection lands. */
  selectByRowKey: (rowKey: string, anchorEl?: HTMLElement) => void;
  /** True while a deep link or scroll anchor is pending. Suppresses the
   *  scroll-to-top on selection change: the URL-driven case is sync (the URL
   *  update lands before the row-click effects fire, so a top reset would
   *  clobber the about-to-fire imperative scroll). For pure swimlane row
   *  clicks the top reset is desirable — it clears the previous branch's
   *  deep scroll position before the imperative scroll lands. */
  hasScrollTarget: boolean;
}

export function useSelectionActions(
  options: UseSelectionActionsOptions
): SelectionActions {
  const { timelineState, scrollRef, initialEventId, initialMessageId } =
    options;

  // Scroll-anchor for inline fork-navigator clicks: the prefix above the
  // clicked navigator is unchanged across the selection, so capturing and
  // restoring scrollTop keeps the navigator at the same viewport position.
  const [scrollAnchor, setScrollAnchor] = useState<{
    scrollTop: number;
  } | null>(null);

  const hasScrollTarget = !!(
    initialEventId ||
    initialMessageId ||
    scrollAnchor
  );

  const spanSelectKeys = useMemo(
    () => buildSpanSelectKeys(timelineState.rows),
    [timelineState.rows]
  );

  const selectBySpanId = useCallback(
    (spanId: string) => {
      const key = spanSelectKeys.get(spanId);
      if (!key) return;
      timelineState.select(key.key);
    },
    [spanSelectKeys, timelineState]
  );

  const selectByRowKey = useCallback(
    (rowKey: string, anchorEl?: HTMLElement) => {
      if (anchorEl && scrollRef.current) {
        setScrollAnchor({ scrollTop: scrollRef.current.scrollTop });
      }
      timelineState.select(rowKey, { preserveScroll: true });
    },
    [timelineState, scrollRef]
  );

  // Branch selections share one effectiveListId (no remount), so the prefix
  // above the clicked navigator is laid out identically — restoring scrollTop
  // keeps it at the same viewport position.
  // eslint-disable-next-line tsmono/no-raw-use-effect -- baselined at rule introduction; migrate to a named hook or derived state
  useEffect(() => {
    if (!scrollAnchor) return;
    requestAnimationFrame(() => {
      scrollRef.current?.scrollTo({ top: scrollAnchor.scrollTop });
      setScrollAnchor(null);
    });
  }, [scrollAnchor, scrollRef]);

  return { spanSelectKeys, selectBySpanId, selectByRowKey, hasScrollTarget };
}
